<template>
    <div>
      <InventoryHeaderTab />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Product Location</h5>
          <p class="page-sub">View and manage stock levels across all locations</p>
        </div>
        <div class="page-actions">
  
          <!-- Store Filter -->
          <div class="filter-wrap">
            <font-awesome-icon icon="filter" class="filter-icon" />
            <Multiselect
              label="name"
              v-model="tableData.store"
              :options="stores"
              placeholder="Filter by store"
              :searchable="true"
              :close-on-select="true"
              @select="fetchData()"
              class="store-filter-multiselect"
            />
          </div>
  
          <!-- Search -->
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input type="text" class="search-input" placeholder="Search products..." v-model="query" />
          </div>
  
        </div>
      </div>
  
      <!-- Data Card -->
      <div class="data-card">
  
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted">Loading product locations...</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="details.length === 0" class="state-block">
          <div class="state-icon state-icon--blue">
            <font-awesome-icon icon="map-marker-alt" />
          </div>
          <p class="state-title">No product locations found</p>
          <p class="state-sub">Products will appear here once they have been assigned to a store.</p>
        </div>
  
        <!-- Table -->
        <template v-else>
  
          <div class="table-toolbar">
            <span class="table-count">
              Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong> records
            </span>
            <div class="d-flex align-items-center gap-2">
              <label class="table-count">Rows:</label>
              <select v-model="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="30">30</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option :value="pagination.total">All</option>
              </select>
            </div>
          </div>
  
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th style="width:22%">Product Name</th>
                  <th>Store</th>
                  <th>Quantity</th>
                  <th>Pcs/Carton</th>
                  <th>Cartons</th>
                  <th>Pcs</th>
                  <th>Warning Qty</th>
                  <th class="col-actions">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(location, index) in details" :key="index">
  
                  <td>
                    <div class="product-cell">
                      <div class="product-avatar"><font-awesome-icon icon="box" /></div>
                      <span class="product-name">{{ location.product_name }}</span>
                    </div>
                  </td>
  
                  <td>
                    <span class="store-tag">{{ location.store_name }}</span>
                  </td>
  
                  <td>
                    <span class="qty-badge" :class="location.quantity < 1 ? 'qty-zero' : location.quantity < (location.warning_quantity ?? 5) ? 'qty-low' : ''">
                      {{ location.quantity }}
                    </span>
                  </td>
  
                  <td class="text-muted-cell">
                    {{ location.product?.variants?.length ? (location.product.variants.filter(v => v.variant_selling_price == Math.max.apply(Math, location.product.variants.map(v2 => v2.variant_selling_price)))[0]?.variant_quantity ?? 'N/A') : 'N/A' }}
                    {{ calculatePieces(location, location.id) }}
                  </td>
  
                  <td class="text-muted-cell">{{ carton[location.id] === undefined ? 'N/A' : carton[location.id] }}</td>
                  <td class="text-muted-cell">{{ pieces[location.id] === undefined ? 'N/A' : pieces[location.id] }}</td>
  
                  <td>
                    <span v-if="location.warning_quantity" class="warning-badge">
                      <font-awesome-icon icon="exclamation-triangle" class="me-1" />
                      {{ location.warning_quantity }}
                    </span>
                    <span v-else class="text-muted-cell">—</span>
                  </td>
  
                  <td class="col-actions">
                    <button
                      class="action-btn action-btn--edit"
                      :data-bs-toggle="permission.edit_product == 1 ? 'modal' : ''"
                      data-bs-target="#editLocation"
                      @click="getProductId(location)"
                      :disabled="permission.edit_product != 1"
                      title="Edit warning quantity"
                    >
                      <font-awesome-icon icon="pen" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
  
          <div class="table-footer">
            <pagination :pagination="pagination" @prev="tableData.page = pagination.currentPage - 1; fetchData()" @next="tableData.page = pagination.currentPage + 1; fetchData()" />
          </div>
  
        </template>
      </div>
  
      <!-- Edit Warning Qty Modal -->
      <div class="modal fade" id="editLocation" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-sm">
          <div class="modal-content modal-clean">
            <div class="modal-header-clean">
              <div class="modal-title-icon">
                <font-awesome-icon icon="exclamation-triangle" />
              </div>
              <div>
                <h6 class="modal-clean-title">Edit Warning Quantity</h6>
                <p class="modal-clean-sub">Set the minimum stock level before an alert is triggered</p>
              </div>
              <button type="button" class="btn-close ms-auto" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <form @submit.prevent="editLocation">
              <div class="modal-body-clean">
                <div class="field-group">
                  <label class="field-label">Warning Quantity</label>
                  <input
                    type="text"
                    class="field-input"
                    placeholder="e.g. 10"
                    v-model="form.warning_quantity"
                    onkeypress="return /^[0-9]+$/i.test(event.key)"
                  />
                  <span class="field-hint">You'll be alerted when stock falls below this number</span>
                </div>
              </div>
              <div class="modal-footer-clean">
                <button type="button" class="btn-outline" data-bs-dismiss="modal">Cancel</button>
                <button type="submit" class="btn-primary-action" data-bs-dismiss="modal">
                  <font-awesome-icon icon="check" class="me-2" /> Update
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
  
      <SingleDeleteModal :modalId="'singleModal'" @deleteSingle="callSingleProductDeleteButton" :modalText="'Are you sure you want to delete this product?'" :action="'deleteSingle'" />
      <BulkDeleteModal :modalId="'bulkModal'" @deleteBulk="callBulkDeleteProductButton" :modalText="'Are you sure you want to delete the selected products?'" />
    </div>
  </template>
  
  <style src="@vueform/multiselect/themes/default.css"></style>
  
  <script>
  import InventoryHeaderTab from "./component/inventoryheadertab.vue";
  import axios from "axios";
  import { useToast } from "vue-toastification";
  import SingleDeleteModal from "../../../components/ActionModal.vue";
  import BulkDeleteModal from "../../../components/bulkDeleteModal.vue";
  import { reusableMixin } from "../../../lib/reusableMixin";
  import { useStore } from "vuex";
  import Multiselect from '@vueform/multiselect';
  
  export default {
    components: { InventoryHeaderTab, SingleDeleteModal, BulkDeleteModal, Multiselect },
    mixins: [reusableMixin],
  
    data() {
      return {
        details: [], loading: false, query: null,
        selectedModel: [], allSelected: false,
        tableData: { page: 1,
          outofstock:    !isNaN(this.$route.query.outofstock)    ? true : false,
          totallowstock: !isNaN(this.$route.query.totallowstock) ? true : false,
          store: 'All', draw: 0, length: 5, search: '',
          column: 0, dir: 'desc', id: '', status: [0,1,2,3], query: '',
        },
        pagination: { lastPage:'', currentPage:'', total:'', lastPageUrl:'', nextPageUrl:'', prevPageUrl:'', from:'', to:'' },
        toast: useToast(), currentID: null, store: useStore(),
        stores: ['All'],
        form: { warning_quantity: '' },
        productId: '', carton: [], pieces: [],
      };
    },
  
    computed: {
      permission() { return this.store.state.getCurrentUserPermission; },
    },
  
    watch: { query() { this.fetchData(); } },
  
    created() { this.fetchData(); this.getStores(); },
  
    methods: {
      calculatePieces(location, id) {
        if (!location.product) return;
        const variant          = location.product.variants;
        let quantity           = location.quantity;
        if (variant.length > 0) {
          const variantQty = variant.filter(v => v.variant_selling_price == Math.max.apply(Math, variant.map(v2 => v2.variant_selling_price)))[0]?.variant_quantity;
          let carton = 0;
          while (parseInt(variantQty) <= quantity) {
            quantity -= variantQty; carton += 1;
            if (quantity < variantQty) break;
          }
          this.pieces[id] = quantity;
          this.carton[id] = carton;
        }
      },
  
      getStores() {
        axios.get('/admin/store/user-stores').then(res => {
          res.data.data.forEach(s => this.stores.push({ value: s.id, name: s.store_name }));
        });
      },
  
      getProductId(product) {
        this.productId = product.id;
        this.getProduct(this.productId);
      },
  
      editLocation() {
        axios.put(`/inventory/product/web/edit-warning-qty/${this.productId}`, { warning_quantity: this.form.warning_quantity })
          .then(res => { this.fetchData(); this.toast.success(res.data.message); })
          .catch(error => { this.toast.error(error?.response?.data?.message ?? 'Something went wrong'); });
      },
  
      selectAll() {
        this.selectedModel = this.allSelected ? this.details.map(u => u.id) : [];
      },
  
      fetchData(url = '/inventory/product/location/web/all') {
        this.loading = true;
        this.tableData.query = this.query;
        this.tableData.draw++;
        axios.get(url, { params: this.tableData })
          .then(response => {
            this.loading = false;
            this.details = response.data.data.data;
            this.configPagination(response.data.data);
          })
          .catch(() => { this.loading = false; });
      },
  
      callSingleProductDeleteButton() {
        axios.delete(`/inventory/product/${this.currentID}`)
          .then(() => { this.toast.success('Product deleted successfully'); this.selectedModel = []; this.fetchData(); })
          .catch(error => { this.toast.error(error?.response?.data?.message ?? 'Something went wrong'); });
      },
  
      callBulkDeleteProductButton() {
        axios.post('/inventory/product/delete-bulk', { products: this.selectedModel })
          .then(response => { this.toast.success(response.data.data); this.selectedModel = []; this.fetchData(); })
          .catch(error => { this.toast.error(error?.response?.data?.message ?? 'Something went wrong'); });
      },
  
      getModalCurrentId(id) { this.currentID = id; },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
  
      getProduct(id) {
        axios.get(`/inventory/product/store-product/${id}`).then(res => {
          this.form.warning_quantity = res.data.data.warning_quantity;
        });
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  /* ── Store Filter ── */
  .filter-wrap { position:relative; display:flex; align-items:center; width:220px; }
  .filter-icon { position:absolute; left:11px; color:#7a90b0; font-size:13px; z-index:2; pointer-events:none; }
  :deep(.store-filter-multiselect) {
    --ms-border-color: #dde5f0; --ms-border-radius: 8px;
    --ms-font-size: 13px; --ms-py: 8px; --ms-px: 12px;
    --ms-ring-color: rgba(0,74,173,.08); padding-left: 28px;
  }
  :deep(.store-filter-multiselect.is-active) { --ms-border-color: #004AAD; }
  
  /* ── Search ── */
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:210px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  .search-input::placeholder { color:#aab8cc; }
  
  /* ── Data Card ── */
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── States ── */
  .state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon  { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
  .state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub   { font-size:13px; color:#7a90b0; margin:0; }
  
  /* ── Toolbar ── */
  .table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count   { font-size:13px; color:#7a90b0; }
  .rows-select   { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }
  
  /* ── Table ── */
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:13px 16px; font-size:13.5px; vertical-align:middle; }
  .col-actions { width:70px; text-align:center; }
  .text-muted-cell { color:#4a5e78; }
  
  /* ── Product Cell ── */
  .product-cell   { display:flex; align-items:center; gap:10px; }
  .product-avatar { width:34px; height:34px; border-radius:8px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
  .product-name   { font-weight:600; color:#1a2b45; }
  
  /* ── Store Tag ── */
  .store-tag { display:inline-block; background:#f4f7fb; color:#4a5e78; border:1px solid #e4eaf2; border-radius:6px; padding:3px 10px; font-size:12.5px; }
  
  /* ── Qty Badge ── */
  .qty-badge { display:inline-block; background:#edf4ff; color:#004AAD; border-radius:20px; padding:2px 10px; font-size:12.5px; font-weight:600; }
  .qty-low   { background:#fff7ed; color:#e08c00; }
  .qty-zero  { background:#fff0f0; color:#e03535; }
  
  /* ── Warning Badge ── */
  .warning-badge { display:inline-flex; align-items:center; background:#fff7ed; color:#e08c00; border:1px solid #fed7aa; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; }
  
  /* ── Action Button ── */
  .action-btn { width:32px; height:32px; border-radius:7px; border:1px solid #e4eaf2; display:flex; align-items:center; justify-content:center; font-size:13px; cursor:pointer; transition:background .15s,border-color .15s; background:#fff; margin:0 auto; }
  .action-btn--edit { color:#004AAD; }
  .action-btn--edit:hover:not(:disabled) { background:#edf4ff; border-color:#c5d6f5; }
  .action-btn--edit:disabled { color:#ccc; cursor:not-allowed; }
  
  /* ── Table Footer ── */
  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
  
  /* ── Modal ── */
  .modal-clean { border-radius:14px; border:none; box-shadow:0 20px 60px rgba(0,0,0,.12); overflow:hidden; }
  .modal-header-clean { display:flex; align-items:flex-start; gap:14px; padding:20px 24px 16px; border-bottom:1px solid #f0f4f8; }
  .modal-title-icon { width:40px; height:40px; border-radius:10px; background:rgba(224,140,0,.1); color:#e08c00; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
  .modal-clean-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .modal-clean-sub   { font-size:12px; color:#7a90b0; margin:0; }
  .modal-body-clean  { padding:20px 24px; }
  .modal-footer-clean { display:flex; align-items:center; justify-content:flex-end; gap:10px; padding:16px 24px; border-top:1px solid #f0f4f8; }
  
  /* ── Field ── */
  .field-group  { display:flex; flex-direction:column; gap:6px; }
  .field-label  { font-size:13px; font-weight:600; color:#1a2b45; }
  .field-hint   { font-size:11.5px; color:#7a90b0; }
  .field-input  { padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; }
  .field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  
  /* ── Buttons ── */
  .btn-primary-action { display:inline-flex; align-items:center; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 20px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-primary-action:hover { background:#003b8e; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 18px; border-radius:8px; cursor:pointer; transition:background .15s; }
  .btn-outline:hover { background:#f4f7fb; }
  
  /* ── Responsive ── */
  @media(max-width:767px) {
    .page-header,.page-actions { flex-direction:column; align-items:stretch; }
    .filter-wrap,.search-input { width:100%; }
  }
  </style>